'use client';

import React from 'react';

interface ResultScreenProps {
  stageName: string;
  mistakes: number;
  timeTaken?: number; // 秒単位 (オプション)
  score?: number; // (オプション)
  onNextStage: () => void; // 次のステージへ進むコールバック
  onRetryStage: () => void; // このステージを再挑戦するコールバック
  onShowOverallPerformance?: () => void; // 総合パフォーマンスサマリを表示するコールバック (追加)
  storyAfter?: string; // ステージクリア後のストーリー
  programmingConcept?: string; // このステージで学んだプログラミングの概念
  finalCode?: string; // 完成したプログラム全体 (追加)
}

const ResultScreen: React.FC<ResultScreenProps> = ({
  stageName,
  mistakes,
  timeTaken,
  score,
  onNextStage,
  onRetryStage,
  onShowOverallPerformance, // 追加
  storyAfter,
  programmingConcept,
  finalCode,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center p-4 z-50">
      <div className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl w-full max-w-lg text-center transform transition-all animate-fadeIn max-h-[90vh] overflow-y-auto">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-green-600 mb-4">
          ステージクリア！🎉
        </h2>
        <p className="text-xl font-semibold text-gray-700 mb-6">{stageName}</p>

        {storyAfter && (
          <div className="my-4 p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="italic text-md text-gray-700">{storyAfter}</p>
          </div>
        )}

        {programmingConcept && (
          <div className="my-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <h4 className="font-semibold text-yellow-700">このステージで学んだこと：</h4>
            <p className="text-sm text-yellow-600">{programmingConcept}</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6 text-lg">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-blue-700 font-semibold">ミスタイプ数:</p>
            <p className="text-2xl font-bold text-blue-600">{mistakes} 回</p>
          </div>
          {timeTaken !== undefined && (
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-purple-700 font-semibold">クリア時間:</p>
              <p className="text-2xl font-bold text-purple-600">{timeTaken} 秒</p>{' '}
              {/* TODO: フォーマット (例: m分s秒) */}
            </div>
          )}
          {score !== undefined && (
            <div className="bg-indigo-50 p-4 rounded-lg col-span-1 sm:col-span-2">
              <p className="text-indigo-700 font-semibold">スコア:</p>
              <p className="text-3xl font-bold text-indigo-600">{score} 点</p>
            </div>
          )}
        </div>

        {finalCode && (
          <div className="my-6">
            <h4 className="font-semibold text-gray-700 mb-2">完成したプログラム:</h4>
            <pre className="text-sm text-left bg-gray-100 p-4 rounded-md whitespace-pre-wrap overflow-x-auto">
              {finalCode}
            </pre>
          </div>
        )}

        <div className="mt-8 space-y-3 sm:space-y-0 sm:space-x-3 flex flex-col sm:flex-row justify-center">
          <button
            onClick={onNextStage}
            className="w-full sm:w-auto px-6 py-3 bg-green-500 text-white font-bold rounded-lg shadow-md hover:bg-green-600 transition-transform transform hover:scale-105 duration-150 text-lg"
          >
            次のステージへ
          </button>
          <button
            onClick={onRetryStage}
            className="w-full sm:w-auto px-6 py-3 bg-yellow-500 text-white font-bold rounded-lg shadow-md hover:bg-yellow-600 transition-transform transform hover:scale-105 duration-150 text-lg"
          >
            もう一度挑戦
          </button>
          {onShowOverallPerformance && (
            <button
              onClick={onShowOverallPerformance}
              className="w-full sm:w-auto px-6 py-3 bg-blue-500 text-white font-bold rounded-lg shadow-md hover:bg-blue-600 transition-transform transform hover:scale-105 duration-150 text-lg mt-3 sm:mt-0"
            >
              結果を見る
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultScreen;
